import Chip from '@mui/material/Chip'
import Icon from '@mui/material/Icon'
import { styled } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import { isEmpty, pathOr } from 'ramda'
import React from 'react'
import Dropzone, { Accept, DropzoneRef } from 'react-dropzone'

import { FilesType } from '@/app/types'

type Props = {
  accept?: Accept
  currentFile?: string | undefined
  customError?: React.ReactNode
  extensions?: string[]
  files?: FilesType
  maxFileSize?: React.ReactNode
  namespace?: string
  uploadContent?: React.ReactNode
  uploading?: boolean
  uploadRef?: React.RefObject<DropzoneRef>
  onAccept: (file: { file: File; filename: string }) => void
  onCancel?: () => void
}

const defaultFile = {
  file: null,
  filename: '',
  error: ' ', // not truthy
}

const Upload: React.FC<Props> = ({
  accept,
  currentFile,
  customError = '',
  extensions = [],
  files = {},
  maxFileSize = '',
  namespace = '0',
  uploadContent,
  uploading = false,
  uploadRef,
  onAccept,
  onCancel,
}) => {
  const [state, setState] = React.useState<{
    file: File | null
    filename: string
    error: React.ReactNode
  }>(defaultFile)

  React.useEffect(() => {
    if (!uploading && (!files || isEmpty(files) || !currentFile)) {
      setState(defaultFile)
    }
  }, [currentFile, files, uploading])

  return (
    <div
      id={`upload-${namespace}`}
      style={{
        position: 'relative',
        overflow: 'hidden',
        width: '100%',
        height: '100%',
      }}
    >
      <Dropzone
        accept={accept}
        multiple={false}
        ref={uploadRef}
        onDrop={([fileDropped]) => {
          if (!fileDropped) {
            return
          }
          const filename = pathOr<string>('', ['name'], fileDropped)
          if (filename && extensions.indexOf(filename.split('.')?.pop()?.toLowerCase() || '') > -1) {
            setState({
              file: fileDropped,
              filename,
              error: '',
            })
            onAccept({
              file: fileDropped,
              filename,
            })
            return
          }
          setState({
            file: fileDropped,
            filename,
            error: customError || (
              <span>
                File type is invalid.
                <br />
                Currently, we support files with an extension of{' '}
                {extensions.map((ext, i) => (
                  <span>
                    <b>{ext}</b>
                    {i === ext.length - 1 ? '.' : ', '}
                  </span>
                ))}
              </span>
            ),
          })
        }}
      >
        {({ getRootProps, getInputProps }) => (
          <DropContainer {...getRootProps()} isHidden={!!uploadContent} hasError={!!state.error}>
            <input {...getInputProps()} style={{ display: 'none' }} />
            <DropIcon>cloud_upload</DropIcon>
            {state.error && (
              <DropText>
                <Typography gutterBottom={true}>{state.error}</Typography>
              </DropText>
            )}
            {!state.error && state.file && (
              <DropText>
                <Typography gutterBottom={true}>{'File awaiting upload:'}</Typography>
                {state.filename && (
                  <Chip
                    label={state.filename}
                    onDelete={() => {
                      setState({
                        file: null,
                        filename: '',
                        error: '',
                      })
                      onCancel?.()
                    }}
                  />
                )}
              </DropText>
            )}
            {!state.file && (
              <DropText id="to-upload-text">
                <Typography>Drop a file here, or click here to select one</Typography>
                <Typography color="textSecondary">Supported types: {extensions.join(', ')}</Typography>
                {maxFileSize && <Typography color="textSecondary">Max file size: {maxFileSize}</Typography>}
              </DropText>
            )}
          </DropContainer>
        )}
      </Dropzone>
      <UploadButton>{uploadContent}</UploadButton>
    </div>
  )
}

const DropContainer = styled('div', {
  shouldForwardProp: (prop) => prop !== 'hasError' && prop !== 'isHidden',
})<{ hasError: boolean; isHidden: boolean }>(({ hasError, isHidden, theme }) => ({
  display: isHidden ? 'none' : 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
  height: '100%',
  backgroundColor: theme.palette.grey[100],
  color: theme.palette.common.white,
  borderRadius: '4px',
  border: '2px dashed',
  borderColor: hasError ? theme.palette.error.main : theme.palette.grey[300],
  cursor: 'pointer',
  padding: '0px 15px',
}))

const DropIcon = styled(Icon)({
  position: 'absolute',
  fontSize: '5rem',
})

const DropText = styled('div')({
  zIndex: 1,
  textAlign: 'center',
  color: 'rgba(0, 0, 0, 0.87)',
})

const UploadButton = styled('div')({
  marginTop: '18px',
})

export default Upload
